import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Clock3,
  Cookie,
  Edit3,
  Gauge,
  Home,
  Mail,
  MapPin,
  Phone,
  RotateCcw,
  Shield,
  ShoppingCart,
  Star,
  User,
  Wind,
  Wrench,
  Zap,
} from "lucide-react";
import "./styles.css";
import { bookAppointment, createLead, fetchAvailableAppointments, fetchWorkshopConfig, prepareAppointmentBooking } from "./api";

const DEFAULT_TRUST_ELEMENTS = [
  { topText: "23", bottomText: "Jahre Erfahrung" },
  { topText: "12.000+", bottomText: "Zufriedene Kunden" },
  { topText: "4,9 ★", bottomText: "Google Bewertung" },
  { topText: "Garantierte", bottomText: "Kundenzufriedenheit" },
];

const DEFAULT_GOOGLE_RATING = {
  score: 4.9,
  reviewCount: 423,
};

const FALLBACK_WORKSHOP = {
  id: "",
  slug: "",
  name: "",
  settings: {
    branding: {
      logoUrl: "",
      headerTitle: "Terminanfrage",
      primaryColor: "#111110",
      accentColor: "#706a58",
      headerColor: "#111110",
    },
    contact: {
      street: "",
      city: "",
      phoneDisplay: "",
      phoneHref: "",
      email: "",
      openingHours: [],
    },
    funnel: {
      headline: "",
      subtitle: "",
      trustElements: DEFAULT_TRUST_ELEMENTS,
      googleRating: DEFAULT_GOOGLE_RATING,
      services: [],
    },
    tracking: {
      googleAnalyticsId: "",
      metaPixelId: "",
    },
  },
};

const PUBLIC_FUNNEL_UNAVAILABLE_CODE = 'funnel_unavailable';
const PUBLIC_FUNNEL_UNAVAILABLE_MESSAGE = 'Diese Seite ist aktuell nicht erreichbar. Bitte versuchen Sie es in wenigen Minuten erneut.';
const PUBLIC_FUNNEL_UNAVAILABLE_TITLE = 'Seite aktuell nicht erreichbar';

const COOKIE_CONSENT_STORAGE_KEY = "liya_cookie_consent_v1";
const CONSENT_POLICY_VERSION = "2026-04";
const REMINDER_SERVICE_MARKETING_PREFERENCE = "reminders_and_offers";
const REMINDER_SERVICE_MARKETING_COPY_VARIANT = "REMIND1";
const FUNNEL_STEP_QUERY_PARAM = "step";
const FUNNEL_STATE_STORAGE_KEY_PREFIX = "liya_funnel_state_v1";
const FUNNEL_STORAGE_SCOPE = String(process.env.REACT_APP_WORKSHOP_SLUG || "").trim().toLowerCase();
const DEFAULT_FUNNEL_PAGE = 1;
const FUNNEL_PAGE_TO_STEP = {
  1: "services",
  2: "appointment",
  3: "contact",
  4: "confirm",
  5: "done",
};
const FUNNEL_STEP_TO_PAGE = Object.entries(FUNNEL_PAGE_TO_STEP).reduce((mapping, [page, step]) => {
  mapping[step] = Number(page);
  return mapping;
}, {});

function createEmptyFunnelState() {
  return {
    selectedServices: [],
    appointment: null,
    appointmentPreview: null,
    personalInfo: null,
    bookingConfirmation: null,
  };
}

function normalizeFunnelPage(page) {
  const parsedPage = Number(page);
  return Number.isInteger(parsedPage) && FUNNEL_PAGE_TO_STEP[parsedPage] ? parsedPage : DEFAULT_FUNNEL_PAGE;
}

function getStepValueForPage(page) {
  return FUNNEL_PAGE_TO_STEP[normalizeFunnelPage(page)];
}

function getRequestedFunnelPageFromLocation() {
  if (typeof window === "undefined") {
    return DEFAULT_FUNNEL_PAGE;
  }

  const currentUrl = new URL(window.location.href);
  const rawStep = String(currentUrl.searchParams.get(FUNNEL_STEP_QUERY_PARAM) || "").trim().toLowerCase();
  return FUNNEL_STEP_TO_PAGE[rawStep] || DEFAULT_FUNNEL_PAGE;
}

function hasSelectedServices(selectedServices) {
  return Array.isArray(selectedServices) && selectedServices.length > 0;
}

function hasAppointmentSelection(appointment) {
  return Boolean(appointment?.date && appointment?.time);
}

function hasCompletedPersonalInfo(personalInfo) {
  return Boolean(
    personalInfo?.firstName
    && personalInfo?.lastName
    && personalInfo?.email
    && personalInfo?.phone
    && personalInfo?.requiredDataConsent,
  );
}

function getMaxReachableLinearPage(state) {
  if (!hasSelectedServices(state.selectedServices)) {
    return 1;
  }

  if (!hasAppointmentSelection(state.appointment)) {
    return 2;
  }

  if (!hasCompletedPersonalInfo(state.personalInfo)) {
    return 3;
  }

  return 4;
}

function resolveRequestedFunnelPage(requestedPage, state) {
  const normalizedPage = normalizeFunnelPage(requestedPage);
  if (normalizedPage === 5 && state.bookingConfirmation) {
    return 5;
  }

  return Math.min(normalizedPage, getMaxReachableLinearPage(state));
}

function getFunnelStateStorageKey() {
  if (typeof window === "undefined") {
    return "";
  }

  const host = String(window.location.host || "unknown-host").trim().toLowerCase();
  const path = String(window.location.pathname || "/").trim().toLowerCase() || "/";
  const scope = FUNNEL_STORAGE_SCOPE || "host";
  return `${FUNNEL_STATE_STORAGE_KEY_PREFIX}:${host}:${path}:${scope}`;
}

function readStoredFunnelState() {
  const emptyState = createEmptyFunnelState();
  if (typeof window === "undefined") {
    return emptyState;
  }

  try {
    const storageKey = getFunnelStateStorageKey();
    if (!storageKey) {
      return emptyState;
    }

    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) {
      return emptyState;
    }

    const parsed = JSON.parse(raw);
    return {
      selectedServices: Array.isArray(parsed.selectedServices) ? parsed.selectedServices : [],
      appointment: parsed.appointment && typeof parsed.appointment === "object" ? parsed.appointment : null,
      appointmentPreview: parsed.appointmentPreview && typeof parsed.appointmentPreview === "object" ? parsed.appointmentPreview : null,
      personalInfo: parsed.personalInfo && typeof parsed.personalInfo === "object" ? parsed.personalInfo : null,
      bookingConfirmation: parsed.bookingConfirmation && typeof parsed.bookingConfirmation === "object" ? parsed.bookingConfirmation : null,
    };
  } catch {
    return emptyState;
  }
}

function persistFunnelState(state) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const storageKey = getFunnelStateStorageKey();
    if (!storageKey) {
      return;
    }

    const hasPersistableState = (
      hasSelectedServices(state.selectedServices)
      || Boolean(state.appointment)
      || Boolean(state.appointmentPreview)
      || Boolean(state.personalInfo)
      || Boolean(state.bookingConfirmation)
    );

    if (!hasPersistableState) {
      window.sessionStorage.removeItem(storageKey);
      return;
    }

    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        selectedServices: state.selectedServices,
        appointment: state.appointment,
        appointmentPreview: state.appointmentPreview,
        personalInfo: state.personalInfo,
        bookingConfirmation: state.bookingConfirmation,
      }),
    );
  } catch {
    // Ignore storage failures and continue with in-memory state.
  }
}

function syncBrowserHistoryForPage(page, mode = "push") {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedPage = normalizeFunnelPage(page);
  const nextStep = getStepValueForPage(normalizedPage);
  const currentUrl = new URL(window.location.href);
  const currentStep = String(currentUrl.searchParams.get(FUNNEL_STEP_QUERY_PARAM) || "").trim().toLowerCase();
  currentUrl.searchParams.set(FUNNEL_STEP_QUERY_PARAM, nextStep);

  if (mode === "push" && currentStep === nextStep) {
    return;
  }

  const historyMethod = mode === "replace" ? "replaceState" : "pushState";
  window.history[historyMethod](
    { step: nextStep },
    "",
    `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
  );
}

function buildLeadConsentPayload({ marketingConsent, trackingConsent, existingConsent } = {}) {
  const marketingAccepted = Boolean(marketingConsent);

  return {
    dataProcessingAccepted: true,
    marketingAccepted,
    marketingPreference: marketingAccepted ? REMINDER_SERVICE_MARKETING_PREFERENCE : "none",
    marketingCopyVariant: REMINDER_SERVICE_MARKETING_COPY_VARIANT,
    marketingCopyShownAt: existingConsent?.marketingCopyShownAt || new Date().toISOString(),
    trackingAccepted: Boolean(trackingConsent),
    policyVersion: CONSENT_POLICY_VERSION,
  };
}

function readStoredCookieConsent() {
  if (typeof window === "undefined") {
    return { essential: true, analytics: false, decided: false };
  }

  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) {
      return { essential: true, analytics: false, decided: false };
    }

    const parsed = JSON.parse(raw);
    return {
      essential: true,
      analytics: Boolean(parsed.analytics),
      decided: parsed.decided === true,
    };
  } catch {
    return { essential: true, analytics: false, decided: false };
  }
}

function persistCookieConsent(consent) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    COOKIE_CONSENT_STORAGE_KEY,
    JSON.stringify({
      essential: true,
      analytics: Boolean(consent.analytics),
      decided: true,
      decidedAt: new Date().toISOString(),
    }),
  );
}

function getWorkshopViewModel(workshop) {
  const source = workshop || FALLBACK_WORKSHOP;
  const settings = source.settings || {};
  const branding = {
    ...FALLBACK_WORKSHOP.settings.branding,
    ...(settings.branding || {}),
  };
  const contact = {
    ...FALLBACK_WORKSHOP.settings.contact,
    ...(settings.contact || {}),
  };
  const funnel = {
    ...FALLBACK_WORKSHOP.settings.funnel,
    ...(settings.funnel || {}),
  };
  const tracking = {
    ...FALLBACK_WORKSHOP.settings.tracking,
    ...(settings.tracking || {}),
  };

  return {
    id: source.id || FALLBACK_WORKSHOP.id,
    slug: source.slug || FALLBACK_WORKSHOP.slug,
    name: source.name || FALLBACK_WORKSHOP.name,
    branding,
    contact,
    headline: funnel.headline || FALLBACK_WORKSHOP.settings.funnel.headline,
    subtitle: funnel.subtitle || FALLBACK_WORKSHOP.settings.funnel.subtitle,
    trustElements: DEFAULT_TRUST_ELEMENTS.map((fallback, index) => {
      const item = Array.isArray(funnel.trustElements) ? funnel.trustElements[index] : null;
      return {
        topText: typeof item?.topText === "string" ? item.topText : fallback.topText,
        bottomText: typeof item?.bottomText === "string" ? item.bottomText : fallback.bottomText,
      };
    }),
    googleRating: {
      score: Number.isFinite(Number(funnel.googleRating?.score))
        ? Math.max(0, Math.min(Number(funnel.googleRating.score), 5))
        : DEFAULT_GOOGLE_RATING.score,
      reviewCount: Number.isFinite(Number(funnel.googleRating?.reviewCount))
        ? Math.max(Math.trunc(Number(funnel.googleRating.reviewCount)), 0)
        : DEFAULT_GOOGLE_RATING.reviewCount,
    },
    services: Array.isArray(funnel.services) && funnel.services.length > 0
      ? funnel.services.map((service, index) => ({
          id: service.id || `service-${index + 1}`,
          name: service.name || `Service ${index + 1}`,
          price: Number(service.price || 0),
          description: service.description || "",
        }))
      : FALLBACK_WORKSHOP.settings.funnel.services,
    tracking,
  };
}

function isPublicFunnelUnavailableError(error) {
  return error?.code === PUBLIC_FUNNEL_UNAVAILABLE_CODE || error?.message === 'PUBLIC_FUNNEL_UNAVAILABLE';
}

function formatGoogleRatingScore(score) {
  const numericScore = Number(score);
  const normalizedScore = Number.isFinite(numericScore)
    ? Math.max(0, Math.min(numericScore, 5))
    : DEFAULT_GOOGLE_RATING.score;
  const hasFraction = Math.abs(normalizedScore % 1) > 0.001;

  return normalizedScore.toLocaleString("de-DE", {
    minimumFractionDigits: hasFraction ? 1 : 0,
    maximumFractionDigits: 1,
  });
}

function formatGoogleReviewCount(reviewCount) {
  const numericCount = Number(reviewCount);
  const normalizedCount = Number.isFinite(numericCount)
    ? Math.max(Math.trunc(numericCount), 0)
    : DEFAULT_GOOGLE_RATING.reviewCount;
  const suffix = normalizedCount === 1 ? "Bewertung" : "Bewertungen";
  return `${normalizedCount.toLocaleString("de-DE")} ${suffix}`;
}

function RatingStars({ score }) {
  const normalizedScore = Number.isFinite(Number(score))
    ? Math.max(0, Math.min(Number(score), 5))
    : 0;

  return (
    <div className="service-hero-rating-stars">
      {[...Array(5)].map((_, index) => {
        const fillRatio = Math.max(0, Math.min(normalizedScore - index, 1));

        return (
          <span key={index} className="service-hero-rating-star-shell">
            <Star className="service-hero-rating-star service-hero-rating-star-base" fill="currentColor" strokeWidth={1.7} />
            <span className="service-hero-rating-star-fill" style={{ width: `${fillRatio * 100}%` }}>
              <Star className="service-hero-rating-star service-hero-rating-star-active" fill="currentColor" strokeWidth={1.7} />
            </span>
          </span>
        );
      })}
    </div>
  );
}

function formatCompactAppointmentDate(dateString) {
  if (!dateString) {
    return "";
  }

  const date = new Date(`${dateString}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return new Intl.DateTimeFormat("de-DE", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }).format(date);
}

function formatServicePrice(price) {
  const amount = Number(price || 0);

  if (amount === 0) {
    return "GRATIS";
  }

  return `${amount.toFixed(2).replace(".", ",")} €`;
}

function formatTotalServicePrice(price) {
  const amount = Number(price || 0);

  if (amount === 0) {
    return "GRATIS";
  }

  return `ab ${amount.toFixed(2).replace(".", ",")} €`;
}

function splitHeadlineForAccent(headline) {
  const normalized = String(headline || "").trim();
  const words = normalized.split(/\s+/).filter(Boolean);

  if (words.length < 2) {
    return { leading: normalized, accent: "" };
  }

  return {
    leading: words.slice(0, -1).join(" "),
    accent: words[words.length - 1],
  };
}

const CALENDAR_WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function getAppointmentMonthLabel(monthKey) {
  if (!monthKey) {
    return "";
  }

  const [year, month] = monthKey.split("-").map(Number);
  if (!year || !month) {
    return "";
  }

  return new Intl.DateTimeFormat("de-DE", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
}

function buildAppointmentCalendarDays(monthKey, availableDateSet) {
  if (!monthKey) {
    return [];
  }

  const [year, month] = monthKey.split("-").map(Number);
  if (!year || !month) {
    return [];
  }

  const firstDay = new Date(year, month - 1, 1);
  const offset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];

  for (let index = 0; index < offset; index += 1) {
    cells.push({ key: `pad-${monthKey}-${index}`, day: null, dateString: null, isAvailable: false });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const dateString = `${monthKey}-${String(day).padStart(2, "0")}`;
    cells.push({
      key: dateString,
      day,
      dateString,
      isAvailable: availableDateSet.has(dateString),
    });
  }

  while (cells.length % 7 !== 0) {
    cells.push({ key: `tail-${monthKey}-${cells.length}`, day: null, dateString: null, isAvailable: false });
  }

  return cells;
}

function getServiceDisplayTag(service) {
  const haystack = `${service?.name || ""} ${service?.description || ""}`.toLowerCase();

  if (haystack.includes("reifen") || haystack.includes("radwechsel")) {
    return "Reifen";
  }

  if (haystack.includes("brems")) {
    return "Bremsen";
  }

  if (haystack.includes("klima")) {
    return "Klima";
  }

  if (haystack.includes("öl") || haystack.includes("oil")) {
    return "Wartung";
  }

  if (haystack.includes("hu") || haystack.includes("au") || haystack.includes("inspek")) {
    return "Inspektion";
  }

  if (haystack.includes("check") || haystack.includes("fahrzeug")) {
    return "Check";
  }

  return "Service";
}

function getServiceIconComponent(service) {
  const haystack = `${service?.id || ""} ${service?.name || ""} ${service?.description || ""}`.toLowerCase();

  if (haystack.includes("vehicle-check") || haystack.includes("fahrzeugcheck") || haystack.includes("fahrzeug check")) {
    return Shield;
  }

  if (haystack.includes("brake") || haystack.includes("brems")) {
    return Shield;
  }

  if (haystack.includes("tire-change-basic") || haystack.includes("radwechsel")) {
    return RotateCcw;
  }

  if (haystack.includes("tire-change-premium") || haystack.includes("reifenwechsel") || haystack.includes("auswuchten")) {
    return Gauge;
  }

  if (haystack.includes("oil") || haystack.includes("öl")) {
    return Zap;
  }

  if (haystack.includes("inspection") || haystack.includes("hu") || haystack.includes("au") || haystack.includes("inspek")) {
    return CheckCircle;
  }

  if (haystack.includes("ac-service") || haystack.includes("klima")) {
    return Wind;
  }

  return Wrench;
}

function getBookingErrorMessage(error) {
  if (isPublicFunnelUnavailableError(error)) {
    return PUBLIC_FUNNEL_UNAVAILABLE_MESSAGE;
  }

  const message = error?.message || "Unbekannter Fehler";

  if (message === "Appointment is no longer available" || message === "Appointment not found") {
    return "Der gewählte Termin ist leider nicht mehr verfügbar. Bitte wählen Sie einen anderen Termin aus.";
  }

  if (message === "Booking-Token ist ungültig oder bereits verwendet") {
    return "Die Buchung konnte nicht sicher abgeschlossen werden. Bitte wählen Sie den Termin erneut aus.";
  }

  if (message === "Booking-Token ist abgelaufen") {
    return "Ihre Buchungssitzung ist abgelaufen. Bitte wählen Sie den Termin erneut aus.";
  }

  if (message === "appointmentId is required") {
    return "Es konnte kein gültiger Termin übermittelt werden. Bitte wählen Sie den Termin erneut aus.";
  }

  if (message === "bookingToken is required") {
    return "Die Buchung konnte nicht bestätigt werden. Bitte versuchen Sie es erneut.";
  }

  if (message === "email is required" || message === "email must be valid") {
    return "Bitte geben Sie eine gültige E-Mail-Adresse an, damit wir Ihre Buchung sicher zuordnen können.";
  }

  if (message === "Failed to prepare appointment booking") {
    return "Die Buchung konnte nicht vorbereitet werden. Bitte versuchen Sie es in wenigen Minuten erneut.";
  }

  if (message === "Failed to book appointment") {
    return "Die Buchung konnte nicht abgeschlossen werden. Bitte versuchen Sie es in wenigen Minuten erneut.";
  }

  if (message === "Failed to create lead") {
    return "Ihre Buchungsdaten konnten nicht vollständig gespeichert werden. Bitte versuchen Sie es erneut.";
  }

  if (message === "consent is required" || message === "consent.dataProcessingAccepted is required" || message === "consent.dataProcessingAccepted must be accepted") {
    return "Bitte stimmen Sie der Datenverarbeitung fuer Terminabwicklung und Kontaktaufnahme zu.";
  }

  return `Fehler: ${message}`;
}

function initializeGoogleAnalytics(measurementId) {
  if (!measurementId || window.__liyaGaId === measurementId) {
    return;
  }

  if (!document.querySelector(`script[data-ga-id="${measurementId}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.dataset.gaId = measurementId;
    document.head.appendChild(script);
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", measurementId);
  window.__liyaGaId = measurementId;
}

function initializeMetaPixel(pixelId) {
  if (!pixelId || window.__liyaMetaPixelId === pixelId) {
    return;
  }

  if (!window.fbq) {
    window.fbq = function fbq() {
      if (window.fbq.callMethod) {
        window.fbq.callMethod.apply(window.fbq, arguments);
      } else {
        window.fbq.queue.push(arguments);
      }
    };
    if (!window._fbq) {
      window._fbq = window.fbq;
    }
    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = "2.0";
    window.fbq.queue = [];
  }

  if (!document.querySelector(`script[data-meta-pixel-id="${pixelId}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    script.dataset.metaPixelId = pixelId;
    document.head.appendChild(script);
  }

  window.fbq("init", pixelId);
  window.fbq("track", "PageView");
  window.__liyaMetaPixelId = pixelId;
}

function Header({ workshop }) {
  const hasPhoneContact = workshop.contact.phoneDisplay && workshop.contact.phoneHref;
  const headerAccent = workshop.branding.headerColor || "#111111";
  const brandTitle = workshop.name || workshop.branding.headerTitle;
  const hasLogo = Boolean(workshop.branding.logoUrl);

  return (
    <header className="header" style={{ "--header-accent": headerAccent }}>
      <div className="header-content">
        <div className="header-brand">
          {hasLogo ? (
            <div className="logo">
              <img src={workshop.branding.logoUrl} alt={brandTitle} className="logo-image" />
            </div>
          ) : null}
          <div className="header-brand-copy">
            <h1 className="header-title">{brandTitle}</h1>
          </div>
        </div>
        {hasPhoneContact ? (
          <a className="header-contact" href={`tel:${workshop.contact.phoneHref}`}>
            <Phone className="header-contact-icon" strokeWidth={2} aria-hidden="true" />
            <span className="header-contact-value">{workshop.contact.phoneDisplay}</span>
          </a>
        ) : null}
      </div>
    </header>
  );
}

function ServiceCard({ service, isSelected, onToggle }) {
  const isFreeService = Number(service.price || 0) === 0;
  const priceColor = isFreeService ? "#7A6200" : "#111827";
  const serviceTag = getServiceDisplayTag(service);
  const ServiceIcon = getServiceIconComponent(service);

  return (
    <div
      className={`service-card ${isSelected ? "selected" : ""}`}
      onClick={() => onToggle(service)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle(service);
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
    >
      <span className="service-card-accent" aria-hidden="true"></span>
      <div className="service-card-index" aria-hidden="true">
        <div className={`service-card-icon-frame ${isFreeService ? "highlight" : ""}`}>
          <ServiceIcon className="service-card-icon" strokeWidth={2.05} />
        </div>
      </div>

      <div className="service-card-body">
        <div className="service-card-tags">
          <span className="service-chip">{serviceTag}</span>
          {isFreeService ? <span className="service-chip service-chip-highlight">Kostenlos</span> : null}
        </div>

        <div className="service-info">
          <h3>{service.name}</h3>
          <p className="description">{service.description}</p>
        </div>
      </div>

      <div className="service-card-meta">
        <p className="price" style={{ color: priceColor }}>
          {formatServicePrice(service.price)}
        </p>
      </div>
    </div>
  );
}

function Page1Services({ workshop, selectedServices, onToggleService, sidebarContent }) {
  const heroHeadline = splitHeadlineForAccent(workshop.headline);
  const googleRatingScore = formatGoogleRatingScore(workshop.googleRating?.score);
  const googleRatingCount = formatGoogleReviewCount(workshop.googleRating?.reviewCount);

  return (
    <div className="page-content service-selection-page">
      <section className="service-hero">
        <div className="service-hero-shell">
          <div className="service-hero-content">
            <div className="service-hero-kicker">
              <span className="service-hero-line"></span>
              <span>Schnell · Zuverlässig · Innovativ</span>
            </div>

            <h2 className="service-hero-title">
              {heroHeadline.leading}
              {heroHeadline.accent ? (
                <>
                  <br />
                  <span className="service-hero-title-outline">{heroHeadline.accent}</span>
                </>
              ) : null}
            </h2>
            <p className="service-hero-copy">{workshop.subtitle}</p>
          </div>

          <div className="service-hero-rating" aria-hidden="true">
            <RatingStars score={workshop.googleRating?.score} />
            <strong className="service-hero-rating-score">{googleRatingScore}</strong>
            <span className="service-hero-rating-source">Google</span>
            <span className="service-hero-rating-divider"></span>
            <span className="service-hero-rating-count">{googleRatingCount}</span>
          </div>
        </div>
      </section>

      <div className="service-trust-strip">
        <div className="service-trust-strip-inner">
          {workshop.trustElements.map((item, index) => (
            <div key={`${item.topText}-${item.bottomText}-${index}`} className="service-trust-item">
              <strong className="service-trust-value">{item.topText}</strong>
              <span className="service-trust-label">{item.bottomText}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="page-one-body">
        <div className="page-one-services-column">
          <div className="service-list-header">
            <p className="service-list-kicker">Leistungsübersicht 2026</p>
            <h3>Unsere Leistungen</h3>
            <p className="service-list-note">Wählen Sie eine oder mehrere Leistungen. Ihre Auswahl bleibt für den nächsten Schritt erhalten.</p>
          </div>

          <div className="services-list">
            {workshop.services.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                isSelected={selectedServices.some((selected) => selected.id === service.id)}
                onToggle={onToggleService}
              />
            ))}
          </div>
        </div>

        {sidebarContent ? <div className="page-one-sidebar-slot">{sidebarContent}</div> : null}
      </div>
    </div>
  );
}

function Page2AppointmentCalendar({ workshop, selectedServices, onBack, initialSelection, onSelectionPreviewChange }) {
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [slotError, setSlotError] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => initialSelection?.date || "");
  const [selectedTime, setSelectedTime] = useState(() => initialSelection?.time || "");
  const [visibleMonth, setVisibleMonth] = useState("");
  const workshopIdentity = `${String(workshop?.id || "")}:${String(workshop?.slug || "")}`;

  useEffect(() => {
    const loadSlots = async () => {
      try {
        setLoadingSlots(true);
        setSlotError("");
        const available = await fetchAvailableAppointments();
        setSlots(available);
      } catch (error) {
        setSlotError(
          isPublicFunnelUnavailableError(error)
            ? PUBLIC_FUNNEL_UNAVAILABLE_MESSAGE
            : "Terminslots konnten nicht geladen werden. Bitte später erneut versuchen.",
        );
        setSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    loadSlots();
  }, [workshopIdentity]);

  const availableDates = [...new Set(slots.map((slot) => slot.date))];
  const availableDateSet = new Set(availableDates);
  const sortedDates = [...availableDates].sort();
  const monthKeys = [...new Set(sortedDates.map((date) => String(date).slice(0, 7)))];
  const monthKeySignature = monthKeys.join("|");
  const visibleMonthIndex = monthKeys.indexOf(visibleMonth);
  const calendarDays = buildAppointmentCalendarDays(visibleMonth, availableDateSet);
  const selectedSlot = slots.find((slot) => slot.date === selectedDate && slot.time === selectedTime);
  const timeOptions = slots.filter((slot) => slot.date === selectedDate).map((slot) => slot.time).sort();
  const isSelectedDateAvailable = selectedDate ? availableDateSet.has(selectedDate) : true;

  useEffect(() => {
    const availableMonths = monthKeySignature ? monthKeySignature.split("|") : [];

    if (!availableMonths.length) {
      setVisibleMonth("");
      return;
    }

    setVisibleMonth((currentMonth) => {
      if (currentMonth && availableMonths.includes(currentMonth)) {
        return currentMonth;
      }

      return availableMonths[0];
    });
  }, [monthKeySignature]);

  useEffect(() => {
    if (!onSelectionPreviewChange) {
      return;
    }

    onSelectionPreviewChange({
      date: selectedDate,
      time: selectedTime,
      appointmentId: selectedSlot ? selectedSlot.id : null,
    });
  }, [onSelectionPreviewChange, selectedDate, selectedTime, selectedSlot]);

  const handleMonthChange = (direction) => {
    if (!monthKeys.length) {
      return;
    }

    const nextIndex = visibleMonthIndex + direction;
    if (nextIndex < 0 || nextIndex >= monthKeys.length) {
      return;
    }

    const nextMonth = monthKeys[nextIndex];
    setVisibleMonth(nextMonth);

    if (selectedDate && !selectedDate.startsWith(nextMonth)) {
      setSelectedDate("");
      setSelectedTime("");
    }
  };

  const handleDateSelection = (dateString) => {
    setSelectedDate(dateString);
    setSelectedTime("");
  };

  return (
    <div className="page-content appointment-selection-page">
      <button className="appointment-back-link" onClick={onBack}>
        <ArrowLeft className="appointment-back-link-icon" aria-hidden="true" />
        Zurück zur Auswahl
      </button>

      <div className="appointment-booking-shell">
        <div className="appointment-booking-header">
          <div className="appointment-booking-eyebrow">
            <span className="appointment-booking-line" aria-hidden="true"></span>
            <span>Schritt 2 von 4</span>
          </div>

          <h2 className="appointment-booking-title">
            Wählen Sie
            <br />
            Ihren Termin
          </h2>
        </div>

        {slotError ? <div className="appointment-inline-error">{slotError}</div> : null}

        {loadingSlots ? (
          <div className="appointment-loading-card">
            <div className="appointment-loading-accent"></div>
            <p>Verfügbare Termine werden geladen...</p>
          </div>
        ) : sortedDates.length === 0 ? (
          <div className="appointment-loading-card empty">
            <div className="appointment-loading-accent"></div>
            <p>Aktuell sind keine freien Termine verfügbar. Bitte versuchen Sie es später erneut.</p>
          </div>
        ) : (
          <div className="appointment-panel-stack">
            <section className="appointment-calendar-shell">
              <div className="appointment-calendar-toolbar">
                <div className="appointment-calendar-heading">
                  <Calendar className="appointment-toolbar-icon" aria-hidden="true" />
                  <h3>{getAppointmentMonthLabel(visibleMonth)}</h3>
                </div>

                <div className="appointment-month-controls">
                  <button
                    className="appointment-month-button"
                    onClick={() => handleMonthChange(-1)}
                    disabled={visibleMonthIndex <= 0}
                    aria-label="Vorheriger Monat"
                  >
                    <ChevronLeft className="appointment-month-button-icon" aria-hidden="true" />
                  </button>
                  <button
                    className="appointment-month-button"
                    onClick={() => handleMonthChange(1)}
                    disabled={visibleMonthIndex === -1 || visibleMonthIndex >= monthKeys.length - 1}
                    aria-label="Nächster Monat"
                  >
                    <ChevronRight className="appointment-month-button-icon" aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div className="appointment-weekdays-grid">
                {CALENDAR_WEEKDAY_LABELS.map((day) => (
                  <span key={day} className="appointment-weekday-label">{day}</span>
                ))}
              </div>

              <div className="appointment-calendar-grid">
                {calendarDays.map((cell) => {
                  if (!cell.day) {
                    return <div key={cell.key} className="appointment-day-cell empty" aria-hidden="true"></div>;
                  }

                  const isSelected = cell.dateString === selectedDate;

                  return (
                    <button
                      key={cell.key}
                      className={`appointment-day-cell ${cell.isAvailable ? "available" : "unavailable"} ${isSelected ? "selected" : ""}`}
                      onClick={() => cell.isAvailable && handleDateSelection(cell.dateString)}
                      disabled={!cell.isAvailable}
                    >
                      {cell.day}
                    </button>
                  );
                })}
              </div>

              <p className="appointment-calendar-note">Nur Tage mit freien Slots sind auswählbar. Bereits vorhandene Slot-Prüfung und Weitergabe der Termin-ID bleiben unverändert.</p>
            </section>

            <section className="appointment-times-shell">
              <div className="appointment-calendar-toolbar compact">
                <div className="appointment-calendar-heading">
                  <Clock3 className="appointment-toolbar-icon" aria-hidden="true" />
                  <h3>Uhrzeit wählen</h3>
                </div>
              </div>

              {selectedDate && !isSelectedDateAvailable ? (
                <div className="appointment-inline-error compact">Für dieses Datum gibt es keine freien Termine.</div>
              ) : null}

              {selectedDate ? (
                timeOptions.length > 0 ? (
                  <div className="appointment-times-grid">
                    {timeOptions.map((time) => (
                      <button
                        key={time}
                        className={`appointment-time-button ${selectedTime === time ? "selected" : ""}`}
                        onClick={() => setSelectedTime(time)}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="appointment-loading-card empty compact">
                    <div className="appointment-loading-accent"></div>
                    <p>Für den gewählten Tag gibt es aktuell keine freien Uhrzeiten.</p>
                  </div>
                )
              ) : (
                <div className="appointment-loading-card compact">
                  <div className="appointment-loading-accent"></div>
                  <p>Wählen Sie im Kalender einen verfügbaren Tag aus, um freie Uhrzeiten zu sehen.</p>
                </div>
              )}
            </section>

          </div>
        )}
      </div>
    </div>
  );
}

function Page3PersonalInfo({ onContinue, onBack, trackingConsent, initialData }) {
  const [formData, setFormData] = useState(() => ({
    firstName: initialData?.firstName || "",
    lastName: initialData?.lastName || "",
    email: initialData?.email || "",
    phone: initialData?.phone || "",
    requiredDataConsent: Boolean(initialData?.requiredDataConsent),
    marketingConsent: Boolean(initialData?.marketingConsent),
  }));

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContinue = () => {
    if (formData.firstName && formData.lastName && formData.email && formData.phone && formData.requiredDataConsent) {
      onContinue({
        ...formData,
        consent: buildLeadConsentPayload({
          marketingConsent: formData.marketingConsent,
          trackingConsent,
        }),
      });
    } else {
      alert("Bitte füllen Sie alle Felder aus und bestätigen Sie die Pflicht-Einwilligung.");
    }
  };

  return (
    <div className="page-content page-three-shell">
      <button type="button" className="page-three-back-link" onClick={onBack}>
        <ArrowLeft className="page-three-back-icon" aria-hidden="true" />
        <span>Zurück zur Terminwahl</span>
      </button>

      <div className="page-three-header">
        <div className="page-three-step-marker">
          <span className="page-three-step-line" aria-hidden="true" />
          <span>Schritt 3 von 4</span>
        </div>
        <h2 className="page-three-title">
          Ihre
          <br />
          Kontaktdaten
        </h2>
      </div>

      <div className="page-three-form-shell">
        <div className="page-three-name-grid">
          <div className="form-group page-three-field">
            <label htmlFor="firstName">Vorname *</label>
            <input
              className="page-three-input"
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Max"
            />
          </div>
          <div className="form-group page-three-field">
            <label htmlFor="lastName">Nachname *</label>
            <input
              className="page-three-input"
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Mustermann"
            />
          </div>
        </div>

        <div className="form-group page-three-field">
          <label htmlFor="email">E-Mail-Adresse *</label>
          <div className="page-three-input-wrap">
            <Mail className="page-three-input-icon" aria-hidden="true" />
            <input
              className="page-three-input page-three-input-with-icon"
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="max.mustermann@beispiel.de"
            />
          </div>
        </div>

        <div className="form-group page-three-field">
          <label htmlFor="phone">Telefonnummer *</label>
          <div className="page-three-input-wrap">
            <Phone className="page-three-input-icon" aria-hidden="true" />
            <input
              className="page-three-input page-three-input-with-icon"
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="030 / 123 456 789"
            />
          </div>
        </div>

        <div className="page-three-consent-card">
          <label className={`page-three-consent-row ${formData.requiredDataConsent ? "is-selected" : ""}`}>
            <input
              className="page-three-control-input"
              type="checkbox"
              checked={formData.requiredDataConsent}
              onChange={(event) => setFormData((prev) => ({ ...prev, requiredDataConsent: event.target.checked }))}
            />
            <span className="page-three-control-box" aria-hidden="true">
              {formData.requiredDataConsent ? <Check className="page-three-control-check" /> : null}
            </span>
            <span className="page-three-consent-copy">
              Ich habe die Datenschutzhinweise gelesen und bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage und Terminabwicklung verarbeitet werden.
            </span>
          </label>

          <label className={`page-three-consent-row page-three-consent-row-stack ${formData.marketingConsent ? "is-selected" : ""}`}>
            <input
              className="page-three-control-input"
              type="checkbox"
              checked={formData.marketingConsent}
              onChange={(event) => setFormData((prev) => ({ ...prev, marketingConsent: event.target.checked }))}
            />
            <span className="page-three-consent-inline-choice">
              <span className="page-three-control-box" aria-hidden="true">
                {formData.marketingConsent ? <Check className="page-three-control-check" /> : null}
              </span>
              <span className="page-three-consent-copy-note">Ja, ich möchte den kostenlosen Fahrzeug-Erinnerungsservice nutzen.</span>
            </span>
            <span className="page-three-consent-copy page-three-consent-copy-stack page-three-consent-copy-detail">
              <span>Wir erinnern Sie rechtzeitig an wichtige Termine wie Reifenwechsel, TÜV, Inspektion, Ölwechsel oder Klima-Check.</span>
              <span>Zusätzlich erhalten Sie gelegentlich passende Angebote Ihrer Werkstatt.</span>
              <span>Maximal 2 E-Mails pro Monat. Jederzeit abbestellbar. Kein Spam.</span>
            </span>
          </label>
        </div>

        <div className="page-three-submit-block">
          <button type="button" className="page-three-submit-button" onClick={handleContinue} disabled={!formData.requiredDataConsent}>
            <ArrowRight className="page-three-submit-icon" aria-hidden="true" />
            <span>Weiter</span>
          </button>
          <p className="page-three-required-note">* Pflichtfelder</p>
        </div>
      </div>
    </div>
  );
}

function Page4Confirmation({
  selectedServices,
  appointment,
  personalInfo,
  consentPreferences,
  onConfirm,
  onBack,
  onEditServices,
  onEditAppointment,
  onEditPersonalInfo,
  workshop,
}) {
  const totalPrice = selectedServices.reduce((sum, service) => sum + Number(service.price || 0), 0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const formattedAppointmentDate = (() => {
    if (!appointment?.date) {
      return "";
    }

    const date = new Date(`${appointment.date}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
      return appointment.date;
    }

    return new Intl.DateTimeFormat("de-DE", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  })();

  const handleConfirmClick = async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const leadData = {
        name: `${personalInfo.firstName} ${personalInfo.lastName}`,
        email: personalInfo.email,
        phone: personalInfo.phone,
        notes: `Service: ${selectedServices.map((service) => service.name).join(", ")}, Termin: ${appointment.date} ${appointment.time}, Slot-ID: ${appointment.appointmentId || "n/a"}`,
        consent: buildLeadConsentPayload({
          marketingConsent: personalInfo.marketingConsent,
          trackingConsent: consentPreferences.analytics,
          existingConsent: personalInfo.consent,
        }),
      };

      let leadId = null;

      if (appointment.appointmentId) {
        const preparedBooking = await prepareAppointmentBooking({
          appointmentId: appointment.appointmentId,
          serviceIds: selectedServices.map((service) => service.id).filter(Boolean),
          name: `${personalInfo.firstName} ${personalInfo.lastName}`,
          email: personalInfo.email,
          phone: personalInfo.phone,
          consent: leadData.consent,
        });

        await bookAppointment({
          appointmentId: appointment.appointmentId,
          bookingToken: preparedBooking.bookingToken,
        });
        leadId = preparedBooking.leadId;
      } else {
        const result = await createLead(leadData);
        leadId = result.id;
      }

      onConfirm({
        leadId,
        selectedServices,
        appointment,
        personalInfo,
        totalPrice,
      });
    } catch (error) {
      console.error("Fehler:", error);
      setErrorMessage(`❌ ${getBookingErrorMessage(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-content page-four-shell booking-summary-shell">
      <button type="button" className="page-three-back-link" onClick={onBack}>
        <ArrowLeft className="page-three-back-icon" aria-hidden="true" />
        <span>Zurück zu den Kontaktdaten</span>
      </button>

      <div className="booking-summary-header">
        <div className="page-three-step-marker">
          <span className="page-three-step-line" aria-hidden="true" />
          <span>Schritt 4 von 4</span>
        </div>
        <h2 className="page-three-title booking-summary-title">
          Buchungs-
          <br />
          übersicht
        </h2>
        <p className="booking-summary-subtitle">
          Bitte prüfen Sie noch einmal alle Angaben, bevor Sie Ihre Buchung verbindlich abschließen.
        </p>
      </div>

      <section className="booking-summary-card booking-summary-services-card">
        <div className="booking-summary-card-header booking-summary-card-header-between">
          <div className="booking-summary-card-title-row">
            <Wrench className="booking-summary-card-icon" aria-hidden="true" />
            <h3>Gewählte Leistungen</h3>
          </div>
          <button type="button" className="booking-summary-edit-button" onClick={onEditServices}>
            <Edit3 className="booking-summary-edit-icon" aria-hidden="true" />
            <span>Ändern</span>
          </button>
        </div>

        <div className="booking-summary-card-body">
          <div className="booking-summary-service-list">
            {selectedServices.map((service) => (
              <div key={service.id} className="booking-summary-service-item">
                <div className="booking-summary-service-copy">
                  <h4>{service.name}</h4>
                  <span className="summary-step-two-pill booking-summary-service-pill">{getServiceDisplayTag(service)}</span>
                </div>
                <span className={`booking-summary-service-value ${Number(service.price || 0) === 0 ? "is-free" : ""}`}>{formatServicePrice(service.price)}</span>
              </div>
            ))}
          </div>

          <div className="booking-summary-total-row">
            <span>Gesamt</span>
            <strong>{formatTotalServicePrice(totalPrice)}</strong>
          </div>

          <p className="booking-summary-footnote">* Materialkosten werden separat berechnet</p>
        </div>
      </section>

      <div className="booking-summary-detail-grid">
        <section className="booking-summary-card">
          <div className="booking-summary-card-header booking-summary-card-header-between">
            <div className="booking-summary-card-title-row">
              <Calendar className="booking-summary-card-icon" aria-hidden="true" />
              <h3>Ihr Termin</h3>
            </div>
            <button type="button" className="booking-summary-edit-button" onClick={onEditAppointment}>
              <Edit3 className="booking-summary-edit-icon" aria-hidden="true" />
              <span>Ändern</span>
            </button>
          </div>

          <div className="booking-summary-card-body booking-summary-detail-body">
            <div className="booking-summary-detail-group">
              <span className="booking-summary-detail-label">Datum</span>
              <div className="booking-summary-detail-value booking-summary-detail-value-inline">
                <Calendar className="booking-summary-detail-icon" aria-hidden="true" />
                <span>{formattedAppointmentDate}</span>
              </div>
            </div>

            <div className="booking-summary-detail-group">
              <span className="booking-summary-detail-label">Uhrzeit</span>
              <div className="booking-summary-detail-value booking-summary-detail-value-inline">
                <Clock className="booking-summary-detail-icon" aria-hidden="true" />
                <span>{appointment.time} Uhr</span>
              </div>
            </div>
          </div>
        </section>

        <section className="booking-summary-card">
          <div className="booking-summary-card-header booking-summary-card-header-between">
            <div className="booking-summary-card-title-row">
              <User className="booking-summary-card-icon" aria-hidden="true" />
              <h3>Kontaktdaten</h3>
            </div>
            <button type="button" className="booking-summary-edit-button" onClick={onEditPersonalInfo}>
              <Edit3 className="booking-summary-edit-icon" aria-hidden="true" />
              <span>Ändern</span>
            </button>
          </div>

          <div className="booking-summary-card-body booking-summary-detail-body">
            <div className="booking-summary-detail-group">
              <span className="booking-summary-detail-label">Name</span>
              <div className="booking-summary-detail-value">{personalInfo.firstName} {personalInfo.lastName}</div>
            </div>

            <div className="booking-summary-detail-group">
              <span className="booking-summary-detail-label">E-Mail</span>
              <div className="booking-summary-detail-value booking-summary-detail-value-inline">
                <Mail className="booking-summary-detail-icon" aria-hidden="true" />
                <span>{personalInfo.email}</span>
              </div>
            </div>

            <div className="booking-summary-detail-group">
              <span className="booking-summary-detail-label">Telefon</span>
              <div className="booking-summary-detail-value booking-summary-detail-value-inline">
                <Phone className="booking-summary-detail-icon" aria-hidden="true" />
                <span>{personalInfo.phone}</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="booking-summary-privacy-card">
        <div className="booking-summary-privacy-inner">
          <Shield className="booking-summary-privacy-icon" aria-hidden="true" />
          <div className="booking-summary-privacy-copy">
            <h3>Datenschutz & Sicherheit</h3>
            <p>
              Ihre Daten werden verschlüsselt übertragen und nur für die Terminabwicklung verwendet. Sie erhalten eine Bestätigungs-E-Mail mit allen Details. Mit der Buchung akzeptieren Sie unsere Datenschutzerklärung.
            </p>
          </div>
        </div>
      </section>

      {errorMessage ? <div className="page-four-inline-error booking-summary-inline-error">{errorMessage}</div> : null}

      <div className="booking-summary-actions">
        <button type="button" className="booking-summary-confirm-button" onClick={handleConfirmClick} disabled={isLoading}>
          {isLoading ? (
            <>
              <div className="booking-summary-spinner" aria-hidden="true" />
              <span>Wird gebucht...</span>
            </>
          ) : (
            <>
              <CheckCircle className="booking-summary-confirm-icon" aria-hidden="true" />
              <span>Jetzt verbindlich buchen</span>
            </>
          )}
        </button>

        <p className="booking-summary-action-note">Nach der Buchung erhalten Sie eine Bestätigungs-E-Mail mit allen Details</p>
      </div>
    </div>
  );
}

function ThankYouPage({ bookingConfirmation, onNewBooking, workshop }) {
  const { selectedServices, appointment, personalInfo, totalPrice } = bookingConfirmation;
  const formattedAppointmentDate = appointment?.date ? new Intl.DateTimeFormat("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(`${appointment.date}T00:00:00`)) : "";
  const workshopAddress = [workshop.name, workshop.contact.street, workshop.contact.city].filter(Boolean);
  const openingHours = Array.isArray(workshop.contact.openingHours) ? workshop.contact.openingHours : [];

  return (
    <div className="page-content page-four-shell thank-you-success-shell">
      <div className="page-four-hero">
        <div className="page-four-status-icon" aria-hidden="true">
          <CheckCircle className="page-four-status-icon-mark" />
        </div>
        <div className="page-five-status-label">Buchung erfolgreich</div>
        <h2 className="page-four-title">
          Vielen Dank,
          <br />
          {personalInfo.firstName}!
        </h2>
        <p className="page-four-subtitle">
          Ihre Buchung wurde erfolgreich abgeschlossen. Sie erhalten in Kürze eine Bestätigungs-E-Mail mit allen Details an {" "}
          <span className="page-five-email-highlight">{personalInfo.email}</span>.
        </p>
      </div>

      <div className="page-four-overview-grid">
        <section className="page-four-detail-card">
          <div className="page-four-detail-card-header">
            <Calendar className="page-four-detail-card-icon" aria-hidden="true" />
            <h3>Ihr Termin</h3>
          </div>
          <div className="page-four-detail-card-body">
            <span className="page-four-detail-label">Termin</span>
            <div className="page-four-appointment-row">
              <div className="page-four-appointment-meta">
                <Calendar className="page-four-meta-icon" aria-hidden="true" />
                <span>{formattedAppointmentDate}</span>
              </div>
              <div className="page-four-appointment-meta">
                <Clock className="page-four-meta-icon" aria-hidden="true" />
                <span>{appointment.time} Uhr</span>
              </div>
            </div>
          </div>
        </section>

        <section className="page-four-detail-card">
          <div className="page-four-detail-card-header">
            <User className="page-four-detail-card-icon" aria-hidden="true" />
            <h3>Ihre Kontaktdaten</h3>
          </div>
          <div className="page-four-detail-card-body page-four-contact-details">
            <div className="page-four-contact-group">
              <span className="page-four-detail-label">Name</span>
              <div className="page-four-detail-value">{personalInfo.firstName} {personalInfo.lastName}</div>
            </div>
            <div className="page-four-contact-group">
              <span className="page-four-detail-label">E-Mail</span>
              <div className="page-four-detail-value">{personalInfo.email}</div>
            </div>
            <div className="page-four-contact-group">
              <span className="page-four-detail-label">Telefon</span>
              <div className="page-four-detail-value">{personalInfo.phone}</div>
            </div>
          </div>
        </section>
      </div>

      <section className="page-four-services-card">
        <div className="page-four-services-header">
          <div className="page-four-services-kicker">
            <Wrench className="page-four-services-kicker-icon" aria-hidden="true" />
            <span>Gebuchte Leistungen</span>
          </div>
          <h3>Ihre Services</h3>
        </div>

        <div className="page-four-services-body">
          <div className="page-four-service-list">
            {selectedServices.map((service) => (
              <div key={service.id} className="page-four-service-item">
                <div className="page-four-service-copy">
                  <h4>{service.name}</h4>
                  {service.description ? <p>{service.description}</p> : null}
                </div>
                <span className={`page-four-service-value ${Number(service.price || 0) === 0 ? "is-free" : ""}`}>{formatServicePrice(service.price)}</span>
              </div>
            ))}
          </div>

          <div className="page-four-total-row">
            <span>Gesamt</span>
            <strong>{formatTotalServicePrice(totalPrice)}</strong>
          </div>

          <p className="page-four-footnote">* Materialkosten werden separat berechnet</p>
        </div>
      </section>

      <section className="page-four-workshop-section">
        <h3>Sie finden uns hier</h3>

        <div className="page-four-workshop-grid">
          <div className="page-four-workshop-block">
            <div className="page-four-workshop-label-row">
              <MapPin className="page-four-workshop-icon" aria-hidden="true" />
              <span>Adresse</span>
            </div>
            <div className="page-four-workshop-copy">
              {workshopAddress.map((entry) => (
                <p key={entry}>{entry}</p>
              ))}
            </div>
          </div>

          <div className="page-four-workshop-block">
            <div className="page-four-workshop-label-row">
              <Clock className="page-four-workshop-icon" aria-hidden="true" />
              <span>Öffnungszeiten</span>
            </div>
            <div className="page-four-workshop-copy">
              {openingHours.map((entry) => (
                <p key={entry}>{entry}</p>
              ))}
            </div>
          </div>

          <div className="page-four-workshop-block">
            <div className="page-four-workshop-label-row">
              <Phone className="page-four-workshop-icon" aria-hidden="true" />
              <span>Telefon</span>
            </div>
            <div className="page-four-workshop-copy">
              <a href={`tel:${workshop.contact.phoneHref}`}>{workshop.contact.phoneDisplay}</a>
            </div>
          </div>

          <div className="page-four-workshop-block">
            <div className="page-four-workshop-label-row">
              <Mail className="page-four-workshop-icon" aria-hidden="true" />
              <span>E-Mail</span>
            </div>
            <div className="page-four-workshop-copy">
              <a href={`mailto:${workshop.contact.email}`}>{workshop.contact.email}</a>
            </div>
          </div>
        </div>
      </section>

      <div className="page-four-actions">
        <button type="button" className="page-four-confirm-button page-five-home-button" onClick={onNewBooking}>
          <Home className="page-four-action-icon" aria-hidden="true" />
          <span>Zurück zur Startseite</span>
        </button>
        <p className="page-four-action-note">Haben Sie Fragen? Rufen Sie uns gerne an oder schreiben Sie uns eine E-Mail.</p>
      </div>
    </div>
  );
}

function SummaryCard({ selectedServices, appointment, appointmentPreview, personalInfo, currentPage, onContinue }) {
  const totalPrice = selectedServices.reduce((sum, service) => sum + Number(service.price || 0), 0);
  const isStepOne = currentPage === 1;
  const isStepTwo = currentPage === 2;
  const isStepThree = currentPage === 3;
  const stepTwoTotalValue = totalPrice === 0 ? "GRATIS" : `ab ${totalPrice.toFixed(2).replace(".", ",")} €`;
  const stepTwoDate = appointmentPreview?.date || appointment?.date || "";
  const stepTwoTime = appointmentPreview?.time || appointment?.time || "";
  const hasStepTwoPreview = Boolean(stepTwoDate || stepTwoTime);
  const stepThreeDate = appointment?.date || "";
  const stepThreeTime = appointment?.time || "";
  const hasStepThreeAppointment = Boolean(stepThreeDate || stepThreeTime);

  const summaryHeader = (
    <div className="summary-card-step-one-highlight">
      <div className="summary-card-highlight-kicker">
        <ShoppingCart className="summary-card-highlight-icon" aria-hidden="true" />
        <span>Ihre Auswahl</span>
      </div>
      <div className="summary-card-title-row">
        <h3>Zusammenfassung</h3>
      </div>
    </div>
  );

  const stepOneSummaryHeader = (
    <div className="summary-card-step-two-highlight summary-card-step-one-highlight">
      <div className="summary-card-step-two-kicker">
        <ShoppingCart className="summary-card-step-two-kicker-icon" aria-hidden="true" />
        <span>Ihre Auswahl</span>
      </div>
      <h3 className="summary-card-step-two-heading">Zusammenfassung</h3>
    </div>
  );

  const stepTwoSummaryHeader = (
    <div className="summary-card-step-two-highlight">
      <div className="summary-card-step-two-kicker">
        <Calendar className="summary-card-step-two-kicker-icon" aria-hidden="true" />
        <span>Buchungsübersicht</span>
      </div>
      <h3 className="summary-card-step-two-heading">Ihre Buchung</h3>
    </div>
  );

  if (selectedServices.length === 0 && isStepOne) {
    return (
      <div className="summary-card summary-card-step-one empty">
        {stepOneSummaryHeader}
        <div className="summary-card-step-one-empty">
          <div className="summary-card-empty-symbol" aria-hidden="true">
            <ShoppingCart className="summary-card-empty-icon" strokeWidth={2.1} />
          </div>
          <p className="empty-message">Wählen Sie links eine oder mehrere Leistungen aus. Ihre Auswahl erscheint hier sofort.</p>
        </div>
      </div>
    );
  }

  if (isStepOne) {
    return (
      <div className="summary-card summary-card-step-one">
        {stepOneSummaryHeader}

        <div className="summary-card-step-two-body summary-card-step-one-body">
          <div className="summary-card-step-two-list summary-card-step-one-list">
            {selectedServices.map((service) => (
              <div key={service.id} className="summary-item summary-item-step-one summary-item-step-two">
                <div className="summary-item-copy">
                  <span className="summary-step-one-pill summary-step-two-pill">{getServiceDisplayTag(service)}</span>
                  <span className="summary-label">{service.name}</span>
                </div>
                <span className={`summary-value ${Number(service.price || 0) === 0 ? "summary-value-free" : ""}`}>{formatServicePrice(service.price)}</span>
              </div>
            ))}
          </div>

          <div className="summary-total-row summary-total-row-step-one summary-total-row-step-two">
            <span>Gesamt</span>
            <strong>{formatServicePrice(totalPrice)}</strong>
          </div>

          <button className="summary-card-step-one-cta" onClick={onContinue}>
            Termin wählen
            <ArrowRight className="summary-card-cta-icon" aria-hidden="true" />
          </button>
          <p className="summary-card-step-two-footnote summary-card-step-one-footnote">Im nächsten Schritt wählen Sie Ihren Wunschtermin. Materialkosten werden separat berechnet.</p>
        </div>
      </div>
    );
  }

  if (isStepTwo) {
    return (
      <div className="summary-card summary-card-step-two">
        {stepTwoSummaryHeader}

        <div className="summary-card-step-two-body">
          <div className="summary-card-step-two-list">
            {selectedServices.map((service) => (
              <div key={service.id} className="summary-item summary-item-step-two">
                <div className="summary-item-copy">
                  <span className="summary-step-two-pill">{getServiceDisplayTag(service)}</span>
                  <span className="summary-label">{service.name}</span>
                </div>
                <span className={`summary-value ${Number(service.price || 0) === 0 ? "summary-value-free" : ""}`}>{formatServicePrice(service.price)}</span>
              </div>
            ))}
          </div>

          {hasStepTwoPreview ? (
            <div className="summary-card-step-two-appointment">
              <span className="summary-card-step-two-section-label">Ihr Termin</span>
              <div className="summary-card-step-two-appointment-row">
                <div className={`summary-card-step-two-meta ${stepTwoDate ? "" : "pending"}`}>
                  <Calendar className="summary-card-step-two-meta-icon" aria-hidden="true" />
                  <span>{stepTwoDate ? formatCompactAppointmentDate(stepTwoDate) : "Datum noch offen"}</span>
                </div>
                <div className={`summary-card-step-two-meta ${stepTwoTime ? "" : "pending"}`}>
                  <Clock3 className="summary-card-step-two-meta-icon" aria-hidden="true" />
                  <span>{stepTwoTime ? `${stepTwoTime} Uhr` : "Uhrzeit noch offen"}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="summary-card-step-two-appointment pending">
              <span className="summary-card-step-two-section-label">Ihr Termin</span>
              <p>Datum und Uhrzeit noch offen.</p>
            </div>
          )}

          <div className="summary-total-row summary-total-row-step-two">
            <span>Gesamt</span>
            <strong>{stepTwoTotalValue}</strong>
          </div>

          <button className="summary-card-step-two-cta" onClick={onContinue} disabled={!stepTwoDate || !stepTwoTime}>
            {stepTwoDate && stepTwoTime ? "Weiter zu den Kontaktdaten" : "Datum & Uhrzeit wählen"}
            <ArrowRight className="summary-card-cta-icon" aria-hidden="true" />
          </button>

          <p className="summary-card-step-two-footnote">* Materialkosten werden separat berechnet</p>
        </div>
      </div>
    );
  }

  if (isStepThree) {
    return (
      <div className="summary-card summary-card-step-three">
        <div className="summary-card-step-two-highlight">
          <div className="summary-card-step-two-kicker">
            <ShoppingCart className="summary-card-step-two-kicker-icon" aria-hidden="true" />
            <span>Buchungsübersicht</span>
          </div>
          <h3 className="summary-card-step-two-heading">Zusammenfassung</h3>
        </div>

        <div className="summary-card-step-two-body summary-card-step-three-body">
          <div className="summary-card-step-two-list">
            {selectedServices.map((service) => (
              <div key={service.id} className="summary-item summary-item-step-two">
                <div className="summary-item-copy">
                  <span className="summary-step-two-pill">{getServiceDisplayTag(service)}</span>
                  <span className="summary-label">{service.name}</span>
                </div>
                <span className={`summary-value ${Number(service.price || 0) === 0 ? "summary-value-free" : ""}`}>{formatServicePrice(service.price)}</span>
              </div>
            ))}
          </div>

          {hasStepThreeAppointment ? (
            <div className="summary-card-step-two-appointment summary-card-step-three-appointment">
              <span className="summary-card-step-two-section-label">Ihr Termin</span>
              <div className="summary-card-step-two-appointment-row">
                <div className={`summary-card-step-two-meta ${stepThreeDate ? "" : "pending"}`}>
                  <Calendar className="summary-card-step-two-meta-icon" aria-hidden="true" />
                  <span>{stepThreeDate ? formatCompactAppointmentDate(stepThreeDate) : "Datum noch offen"}</span>
                </div>
                <div className={`summary-card-step-two-meta ${stepThreeTime ? "" : "pending"}`}>
                  <Clock className="summary-card-step-two-meta-icon" aria-hidden="true" />
                  <span>{stepThreeTime ? `${stepThreeTime} Uhr` : "Uhrzeit noch offen"}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="summary-card-step-two-appointment pending summary-card-step-three-appointment">
              <span className="summary-card-step-two-section-label">Ihr Termin</span>
              <p>Datum und Uhrzeit noch offen.</p>
            </div>
          )}

          <div className="summary-total-row summary-total-row-step-two">
            <span>Gesamt</span>
            <strong>{stepTwoTotalValue}</strong>
          </div>

          <p className="summary-card-step-two-footnote summary-card-step-three-footnote">* Materialkosten werden separat berechnet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="summary-card summary-card-standard">
      {summaryHeader}

      <div className="summary-card-standard-body">
        {currentPage > 1 && selectedServices.length > 0 && (
          <>
            <div className="summary-section">
              <h4>Services</h4>
              {selectedServices.map((service) => (
                <div key={service.id} className="summary-item">
                  <span className="summary-label">{service.name}</span>
                  <span className="summary-value">{Number(service.price || 0).toFixed(2)}€</span>
                </div>
              ))}
              <div className="summary-item total">
                <span className="summary-label"><strong>Gesamt:</strong></span>
                <span className="summary-value"><strong>{totalPrice.toFixed(2)}€</strong></span>
              </div>
            </div>

            {appointment && (
              <div className="summary-section">
                <h4>Termin</h4>
                <div className="summary-item"><span className="summary-label">Datum:</span><span className="summary-value">{appointment.date}</span></div>
                <div className="summary-item"><span className="summary-label">Uhrzeit:</span><span className="summary-value">{appointment.time}</span></div>
              </div>
            )}

            {personalInfo && (
              <div className="summary-section">
                <h4>Kontakt</h4>
                <div className="summary-item"><span className="summary-label">Name:</span><span className="summary-value">{personalInfo.firstName} {personalInfo.lastName}</span></div>
                <div className="summary-item"><span className="summary-label">Email:</span><span className="summary-value" style={{ fontSize: "0.85em" }}>{personalInfo.email}</span></div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ContactInfo({ workshop, compact = false }) {
  return (
    <div className={`contact-panel ${compact ? "compact" : ""}`}>
      <div className="contact-panel-heading">
        <p className="contact-panel-kicker">Kontakt</p>
        <h2>{compact ? "Direkt erreichbar" : "Kontaktieren Sie uns"}</h2>
      </div>
      <div className="contact-panel-list">
        <div className="contact-item">
          <span className="contact-icon-shell" aria-hidden="true">
            <MapPin className="contact-icon" strokeWidth={2} />
          </span>
          <div className="contact-copy">
            <p className="contact-label">Adresse</p>
            <p className="contact-value">{workshop.contact.street}<br />{workshop.contact.city}</p>
          </div>
        </div>
        <div className="contact-item">
          <span className="contact-icon-shell" aria-hidden="true">
            <Phone className="contact-icon" strokeWidth={2} />
          </span>
          <div className="contact-copy">
            <p className="contact-label">Telefon</p>
            <p className="contact-value"><a href={`tel:${workshop.contact.phoneHref}`}>{workshop.contact.phoneDisplay}</a></p>
          </div>
        </div>
        <div className="contact-item">
          <span className="contact-icon-shell" aria-hidden="true">
            <Mail className="contact-icon" strokeWidth={2} />
          </span>
          <div className="contact-copy">
            <p className="contact-label">Email</p>
            <p className="contact-value"><a href={`mailto:${workshop.contact.email}`}>{workshop.contact.email}</a></p>
          </div>
        </div>
        <div className="contact-item">
          <span className="contact-icon-shell" aria-hidden="true">
            <Clock3 className="contact-icon" strokeWidth={2} />
          </span>
          <div className="contact-copy">
            <p className="contact-label">Öffnungszeiten</p>
            <p className="contact-value">
              {workshop.contact.openingHours.map((entry, index) => (
                <React.Fragment key={entry}>
                  {index > 0 && <br />}
                  {entry}
                </React.Fragment>
              ))}
            </p>
          </div>
        </div>
      </div>
      {!compact ? <a className="contact-btn" href={`mailto:${workshop.contact.email}`}>Nachricht senden</a> : null}
    </div>
  );
}

function PageOneFooter({ workshop, onOpenCookieSettings }) {
  return (
    <footer className="page-one-footer">
      <div className="page-one-footer-shell">
        <div className="page-one-footer-brand">
          <span className="page-one-footer-brandname">{workshop.name}</span>
        </div>

        <div className="page-one-footer-meta">
          <span>{workshop.contact.street}, {workshop.contact.city}</span>
          <a href={`tel:${workshop.contact.phoneHref}`}>{workshop.contact.phoneDisplay}</a>
          <a href={`mailto:${workshop.contact.email}`}>{workshop.contact.email}</a>
        </div>

        <div className="page-one-footer-legal">
          <span>© 2026 {workshop.name}</span>
          <a href="#impressum">Impressum</a>
          <a href="#datenschutz">Datenschutz</a>
          <a href="#agb">AGB</a>
          <a href="#widerrufsbelehrung">Widerrufsbelehrung</a>
          <button
            type="button"
            className="page-one-footer-link-button"
            onClick={onOpenCookieSettings}
          >
            Cookie-Einstellungen
          </button>
        </div>
      </div>
    </footer>
  );
}

function Footer({ workshop, onOpenCookieSettings }) {
  return <PageOneFooter workshop={workshop} onOpenCookieSettings={onOpenCookieSettings} />;
}

function CookieBanner({ isOpen, onAcceptAll, onRejectOptional, onOpenSettings }) {
  const analyticsToggleRef = useRef(null);
  const storedConsent = readStoredCookieConsent();

  const handleSavePreferences = () => {
    if (analyticsToggleRef.current?.checked) {
      onAcceptAll();
      return;
    }

    onRejectOptional();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="Cookie-Einstellungen" aria-modal="true">
      <div className="cookie-banner-content">
        <div className="cookie-banner-header">
          <div className="cookie-banner-header-main">
            <Cookie className="cookie-banner-header-icon" aria-hidden="true" />
            <div className="cookie-banner-header-copy">
              <h2>Cookie-Einstellungen</h2>
              <p>Wir verwenden Cookies, um Ihr Erlebnis zu verbessern</p>
            </div>
          </div>
        </div>

        <div className="cookie-banner-body">
          <p className="cookie-banner-intro">
            Wir verwenden Cookies und ähnliche Technologien, um Ihnen die bestmögliche Nutzererfahrung zu bieten.
            Einige Cookies sind für den Betrieb der Website erforderlich, andere helfen uns, die Website und Ihre
            Erfahrung zu verbessern.
          </p>

          <details className="cookie-banner-details">
            <summary className="cookie-banner-details-summary">
              <ChevronDown className="cookie-banner-details-icon cookie-banner-details-icon--closed" aria-hidden="true" />
              <ChevronUp className="cookie-banner-details-icon cookie-banner-details-icon--open" aria-hidden="true" />
              <span className="cookie-banner-details-label cookie-banner-details-label--closed">Details anzeigen</span>
              <span className="cookie-banner-details-label cookie-banner-details-label--open">Details ausblenden</span>
            </summary>

            <div className="cookie-banner-card-stack">
              <div className="cookie-banner-card cookie-banner-card--required">
                <div className="cookie-banner-card-header">
                  <div className="cookie-banner-card-copy">
                    <h3>Notwendige Cookies</h3>
                    <p>
                      Diese Cookies sind für den Betrieb der Website erforderlich und können nicht deaktiviert werden.
                      Sie speichern keine persönlich identifizierbaren Informationen.
                    </p>
                  </div>
                  <div className="cookie-banner-switch cookie-banner-switch--locked" aria-hidden="true">
                    <span className="cookie-banner-switch-surface">
                      <span className="cookie-banner-switch-thumb" />
                    </span>
                  </div>
                </div>
                <p className="cookie-banner-card-note">Beispiele: Session-Management, Sicherheit, Cookie-Einstellungen</p>
              </div>

              <div className="cookie-banner-card">
                <div className="cookie-banner-card-header">
                  <div className="cookie-banner-card-copy">
                    <h3>Funktionale Cookies</h3>
                    <p>
                      Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung, wie z.B. gespeicherte
                      Präferenzen.
                    </p>
                  </div>
                  <div className="cookie-banner-switch cookie-banner-switch--disabled" aria-hidden="true">
                    <span className="cookie-banner-switch-surface">
                      <span className="cookie-banner-switch-thumb" />
                    </span>
                  </div>
                </div>
                <p className="cookie-banner-card-note">Beispiele: Spracheinstellungen, Login-Status</p>
              </div>

              <div className="cookie-banner-card">
                <div className="cookie-banner-card-header">
                  <div className="cookie-banner-card-copy">
                    <h3>Analyse-Cookies</h3>
                    <p>
                      Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, um die
                      Nutzererfahrung zu verbessern.
                    </p>
                  </div>
                  <label className="cookie-banner-switch cookie-banner-switch--interactive">
                    <input
                      ref={analyticsToggleRef}
                      className="cookie-banner-switch-input"
                      type="checkbox"
                      defaultChecked={storedConsent.analytics}
                    />
                    <span className="cookie-banner-switch-surface">
                      <span className="cookie-banner-switch-thumb" />
                    </span>
                  </label>
                </div>
                <p className="cookie-banner-card-note">Beispiele: Google Analytics, Matomo</p>
              </div>

              <div className="cookie-banner-card">
                <div className="cookie-banner-card-header">
                  <div className="cookie-banner-card-copy">
                    <h3>Marketing-Cookies</h3>
                    <p>
                      Diese Cookies werden verwendet, um Ihnen relevante Werbung und Marketinginhalte anzuzeigen.
                    </p>
                  </div>
                  <div className="cookie-banner-switch cookie-banner-switch--disabled" aria-hidden="true">
                    <span className="cookie-banner-switch-surface">
                      <span className="cookie-banner-switch-thumb" />
                    </span>
                  </div>
                </div>
                <p className="cookie-banner-card-note">Beispiele: Google Ads, Facebook Pixel</p>
              </div>
            </div>
          </details>

          <div className="cookie-banner-links">
            <a href="#datenschutz">Datenschutzerklärung</a>
            <a href="#impressum">Impressum</a>
            <a href="#cookie-richtlinie">Cookie-Richtlinie</a>
          </div>
        </div>

        <div className="cookie-banner-footer">
          <div className="cookie-banner-actions">
            <button type="button" className="cookie-banner-action cookie-banner-action--secondary" onClick={onRejectOptional}>
              <span className="cookie-banner-action-label">
                <span>Nur essenzielle</span>
                <span className="cookie-banner-action-label-second">akzeptieren</span>
              </span>
            </button>
            <button type="button" className="cookie-banner-action cookie-banner-action--save" onClick={handleSavePreferences}>
              <span className="cookie-banner-action-label">
                <span>Auswahl</span>
                <span className="cookie-banner-action-label-second">speichern</span>
              </span>
            </button>
            <button type="button" className="cookie-banner-action cookie-banner-action--primary" onClick={onAcceptAll}>
              <span className="cookie-banner-action-label">
                <span>Alle</span>
                <span className="cookie-banner-action-label-second">akzeptieren</span>
              </span>
            </button>
          </div>
          <p className="cookie-banner-footer-note">
            Sie können Ihre Einstellungen jederzeit in den Cookie-Einstellungen ändern.
          </p>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [restoredFunnelState] = useState(() => readStoredFunnelState());
  const [currentPage, setCurrentPage] = useState(() => resolveRequestedFunnelPage(getRequestedFunnelPageFromLocation(), restoredFunnelState));
  const [selectedServices, setSelectedServices] = useState(() => restoredFunnelState.selectedServices);
  const [appointment, setAppointment] = useState(() => restoredFunnelState.appointment);
  const [appointmentPreview, setAppointmentPreview] = useState(() => restoredFunnelState.appointmentPreview);
  const [personalInfo, setPersonalInfo] = useState(() => restoredFunnelState.personalInfo);
  const [bookingConfirmation, setBookingConfirmation] = useState(() => restoredFunnelState.bookingConfirmation);
  const [workshopConfig, setWorkshopConfig] = useState(null);
  const [loadingWorkshop, setLoadingWorkshop] = useState(true);
  const [workshopError, setWorkshopError] = useState("");
  const [cookieConsent, setCookieConsent] = useState(() => readStoredCookieConsent());
  const [cookieBannerOpen, setCookieBannerOpen] = useState(() => !readStoredCookieConsent().decided);
  const currentPageRef = useRef(currentPage);
  const selectedServicesRef = useRef(selectedServices);
  const appointmentRef = useRef(appointment);
  const personalInfoRef = useRef(personalInfo);
  const bookingConfirmationRef = useRef(bookingConfirmation);

  const workshop = workshopConfig ? getWorkshopViewModel(workshopConfig) : null;
  const hasWorkshop = Boolean(workshop);
  const workshopName = workshop?.name || "";
  const workshopHeaderTitle = workshop?.branding?.headerTitle || "";
  const workshopGoogleAnalyticsId = workshop?.tracking?.googleAnalyticsId || "";
  const workshopMetaPixelId = workshop?.tracking?.metaPixelId || "";

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    selectedServicesRef.current = selectedServices;
    appointmentRef.current = appointment;
    personalInfoRef.current = personalInfo;
    bookingConfirmationRef.current = bookingConfirmation;
  }, [selectedServices, appointment, personalInfo, bookingConfirmation]);

  useEffect(() => {
    persistFunnelState({
      selectedServices,
      appointment,
      appointmentPreview,
      personalInfo,
      bookingConfirmation,
    });
  }, [selectedServices, appointment, appointmentPreview, personalInfo, bookingConfirmation]);

  const navigateToPage = useCallback((requestedPage, options = {}) => {
    const overrides = options.stateOverrides || {};
    const resolvedPage = resolveRequestedFunnelPage(
      requestedPage,
      {
        selectedServices: Object.prototype.hasOwnProperty.call(overrides, "selectedServices")
          ? overrides.selectedServices
          : selectedServicesRef.current,
        appointment: Object.prototype.hasOwnProperty.call(overrides, "appointment")
          ? overrides.appointment
          : appointmentRef.current,
        personalInfo: Object.prototype.hasOwnProperty.call(overrides, "personalInfo")
          ? overrides.personalInfo
          : personalInfoRef.current,
        bookingConfirmation: Object.prototype.hasOwnProperty.call(overrides, "bookingConfirmation")
          ? overrides.bookingConfirmation
          : bookingConfirmationRef.current,
      },
    );
    const previousPage = currentPageRef.current;

    if (previousPage === 2 && resolvedPage === 1) {
      setAppointmentPreview(null);
    }

    if (previousPage === 3 && resolvedPage === 2 && appointmentRef.current) {
      setAppointmentPreview(appointmentRef.current);
    }

    currentPageRef.current = resolvedPage;
    setCurrentPage(resolvedPage);

    if (options.syncUrl !== false) {
      syncBrowserHistoryForPage(resolvedPage, options.historyMode || "push");
    }

    return resolvedPage;
  }, []);

  useEffect(() => {
    navigateToPage(getRequestedFunnelPageFromLocation(), { historyMode: "replace" });

    const handlePopState = () => {
      const requestedPage = getRequestedFunnelPageFromLocation();
      const resolvedPage = navigateToPage(requestedPage, { syncUrl: false });

      if (resolvedPage !== requestedPage) {
        syncBrowserHistoryForPage(resolvedPage, "replace");
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigateToPage]);

  useEffect(() => {
    const loadWorkshop = async () => {
      try {
        const data = await fetchWorkshopConfig();
        setWorkshopError("");
        setWorkshopConfig(data);
      } catch (error) {
        console.error(error);
        setWorkshopError(PUBLIC_FUNNEL_UNAVAILABLE_MESSAGE);
        setWorkshopConfig(null);
      } finally {
        setLoadingWorkshop(false);
      }
    };

    loadWorkshop();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [currentPage]);

  useEffect(() => {
    if (!hasWorkshop) {
      document.title = loadingWorkshop ? 'Terminanfrage' : PUBLIC_FUNNEL_UNAVAILABLE_TITLE;
      return;
    }

    document.title = `${workshopName} | ${workshopHeaderTitle}`;

    if (cookieConsent.analytics) {
      initializeGoogleAnalytics(workshopGoogleAnalyticsId);
      initializeMetaPixel(workshopMetaPixelId);
    }
  }, [
    loadingWorkshop,
    hasWorkshop,
    workshopName,
    workshopHeaderTitle,
    workshopGoogleAnalyticsId,
    workshopMetaPixelId,
    cookieConsent.analytics,
  ]);

  const applyCookieConsent = (nextConsent) => {
    const normalized = {
      essential: true,
      analytics: Boolean(nextConsent.analytics),
      decided: true,
    };
    setCookieConsent(normalized);
    persistCookieConsent(normalized);
    setCookieBannerOpen(false);
  };

  const handleToggleService = (service) => {
    setSelectedServices((prev) => {
      const isSelected = prev.some((item) => item.id === service.id);
      if (isSelected) {
        return prev.filter((item) => item.id !== service.id);
      }
      return [...prev, service];
    });
  };

  const handleContinueFromPage1 = () => {
    if (selectedServices.length > 0) {
      navigateToPage(2);
    } else {
      alert("Bitte wählen Sie mindestens eine Dienstleistung aus");
    }
  };

  const handleAppointment = (appointmentData) => {
    setAppointment(appointmentData);
    setAppointmentPreview(appointmentData);
    navigateToPage(3, {
      stateOverrides: {
        appointment: appointmentData,
      },
    });
  };

  const handleContinueFromPage2 = () => {
    if (appointmentPreview?.date && appointmentPreview?.time) {
      handleAppointment(appointmentPreview);
      return;
    }

    alert("Bitte wählen Sie Datum und Uhrzeit");
  };

  const handlePersonalInfo = (infoData) => {
    setPersonalInfo(infoData);
    navigateToPage(4, {
      stateOverrides: {
        personalInfo: infoData,
      },
    });
  };

  const handleEditServicesFromPage4 = () => {
    navigateToPage(1);
  };

  const handleEditAppointmentFromPage4 = () => {
    if (appointment) {
      setAppointmentPreview(appointment);
    }

    navigateToPage(2);
  };

  const handleEditPersonalInfoFromPage4 = () => {
    navigateToPage(3);
  };

  const resetBookingFlow = () => {
    setSelectedServices([]);
    setAppointment(null);
    setAppointmentPreview(null);
    setPersonalInfo(null);
    setBookingConfirmation(null);
    navigateToPage(1, {
      historyMode: "replace",
      stateOverrides: {
        selectedServices: [],
        appointment: null,
        personalInfo: null,
        bookingConfirmation: null,
      },
    });
  };

  const handleConfirm = (confirmationData) => {
    setSelectedServices([]);
    setAppointment(null);
    setAppointmentPreview(null);
    setPersonalInfo(null);
    setBookingConfirmation(confirmationData);
    navigateToPage(5, {
      historyMode: "replace",
      stateOverrides: {
        selectedServices: [],
        appointment: null,
        personalInfo: null,
        bookingConfirmation: confirmationData,
      },
    });
  };

  const handleBack = () => {
    if (currentPage > 1) {
      navigateToPage(currentPage - 1);
    }
  };

  if (loadingWorkshop) {
    return (
      <div className="app-shell-loading">
        <div className="app-shell-card">
          <h1>Terminanfrage</h1>
          <p>Seite wird geladen...</p>
        </div>
      </div>
    );
  }

  if (workshopError || !workshopConfig) {
    return (
      <div className="app-shell-loading">
        <div className="app-shell-card app-shell-card-error">
          <h1>{PUBLIC_FUNNEL_UNAVAILABLE_TITLE}</h1>
          <p>{PUBLIC_FUNNEL_UNAVAILABLE_MESSAGE}</p>
          <div className="button-group">
            <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
              Erneut laden
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pageOneSidebar = (
    <div className="sidebar sidebar-step-one">
      <SummaryCard selectedServices={selectedServices} appointment={appointment} personalInfo={personalInfo} currentPage={currentPage} onContinue={handleContinueFromPage1} />
    </div>
  );

  return (
    <div
      className="app"
      style={{
        "--brand-primary": workshop.branding.primaryColor,
        "--brand-accent": workshop.branding.accentColor,
      }}
    >
      <Header workshop={workshop} />
      <div className={`app-wrapper ${currentPage === 1 ? "page-one-layout" : ""} ${currentPage === 2 ? "page-two-layout" : ""} ${currentPage === 3 ? "page-three-layout" : ""} ${currentPage === 4 ? "page-four-layout" : ""}`}>
        <div className={`main-content ${currentPage === 4 || currentPage === 5 ? "full-width" : ""}`}>
          {currentPage === 1 && <Page1Services workshop={workshop} selectedServices={selectedServices} onToggleService={handleToggleService} sidebarContent={pageOneSidebar} />}
          {currentPage === 2 && <Page2AppointmentCalendar workshop={workshop} selectedServices={selectedServices} onBack={handleBack} initialSelection={appointmentPreview || appointment} onSelectionPreviewChange={setAppointmentPreview} />}
          {currentPage === 3 && <Page3PersonalInfo onContinue={handlePersonalInfo} onBack={handleBack} trackingConsent={cookieConsent.analytics} initialData={personalInfo} />}
          {currentPage === 4 && <Page4Confirmation selectedServices={selectedServices} appointment={appointment} personalInfo={personalInfo} consentPreferences={cookieConsent} onConfirm={handleConfirm} onBack={handleBack} onEditServices={handleEditServicesFromPage4} onEditAppointment={handleEditAppointmentFromPage4} onEditPersonalInfo={handleEditPersonalInfoFromPage4} workshop={workshop} />}
          {currentPage === 5 && bookingConfirmation && <ThankYouPage bookingConfirmation={bookingConfirmation} onNewBooking={resetBookingFlow} workshop={workshop} />}
        </div>

        {currentPage > 1 && currentPage < 5 && currentPage !== 4 && (
          <div className={`sidebar ${currentPage === 1 ? "sidebar-step-one" : ""} ${currentPage === 2 ? "sidebar-step-two" : ""} ${currentPage === 3 ? "sidebar-step-three" : ""}`}>
            <SummaryCard selectedServices={selectedServices} appointment={appointment} appointmentPreview={currentPage === 2 ? appointmentPreview : null} personalInfo={personalInfo} currentPage={currentPage} onContinue={currentPage === 2 ? handleContinueFromPage2 : handleContinueFromPage1} />
            {currentPage !== 2 && currentPage !== 3 ? <ContactInfo workshop={workshop} compact={currentPage === 1} /> : null}
          </div>
        )}
      </div>
      <Footer workshop={workshop} onOpenCookieSettings={() => setCookieBannerOpen(true)} />
      <CookieBanner
        isOpen={cookieBannerOpen}
        onAcceptAll={() => applyCookieConsent({ analytics: true })}
        onRejectOptional={() => applyCookieConsent({ analytics: false })}
        onOpenSettings={() => setCookieBannerOpen(false)}
      />
    </div>
  );
}

const root = createRoot(document.getElementById("root"));
root.render(<App />);

